import { useEffect, useState } from 'react';
import { api } from '../api.ts';

// ============================================================ Passwortregeln
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „vorgabe zum passwort geben wir vor nach den heutigen
//    sicherheitsrichtlinien. das muss auch angezeigt werden bei jeder
//    passwortvergabe."
//
// „BEI JEDER PASSWORTVERGABE" IST DER PUNKT, und deshalb ist das hier ein
// eigenes Teil und kein Absatz in vier Masken. Ein Passwort wird an VIER
// Stellen vergeben:
//
//   1. Der Einladungsbildschirm  (der Neue setzt sein erstes, ohne Anmeldung)
//   2. Der Wechselzwang          (das Büro hat eines gesetzt)
//   3. „Meine Anmeldung"         (jemand ändert sein eigenes)
//   4. Der Innendienst           (jemand setzt eines für einen anderen)
//
// An allen vieren stand bis heute derselbe Satz in vier Abschriften - und in
// einer davon stand schon „zehn Zeichen", als der Server längst zwölf
// verlangte.
//
// DIE REGELN KOMMEN VOM SERVER (`GET /api/passwortregeln`) und stehen NICHT in
// dieser Datei. Sonst hätten wir wieder zwei Listen: der Server verlangt
// etwas, das die Maske nicht anzeigt. Genau dieser Fehler ist bei den
// gefährlichen Konfigurationsschaltern schon einmal passiert.
//
// SIE STEHEN DA, BEVOR GETIPPT WIRD. Eine Regel, die man erst nach dem
// Absenden erfährt, ist eine Fehlermeldung und keine Vorgabe.
//
// UND SIE HAKEN SICH LIVE AB. Wer beim Tippen sieht, was schon erfüllt ist,
// hört auf zu raten. Der Haken ist dabei eine HILFE und kein Urteil: geprüft
// wird am Ende im Server, denn nur er kennt die Sperrliste. Was hier
// abgehakt wird, ist das, was sich im Browser ohne die Liste beantworten
// lässt.

export interface Vorgabe {
  mindestlaenge: number;
  hoechstlaenge: number;
  /** true = die vier Zeichenarten sind Pflicht (Hausvorgabe, Migration 055). */
  zeichenarten?: boolean;
  regeln: { schluessel: string; text: string; warum: string }[];
  keinRhythmus: string;
  stand: string;
}

/**
 * Die Vorgabe holen - einmal je Bildschirm.
 *
 * SIE WIRD GEMERKT, solange die Seite offen ist. Vier Passwortfelder auf
 * derselben Seite sollen nicht viermal fragen; und die Regeln ändern sich
 * nicht zwischen zwei Tastendrücken.
 */
let gemerkt: Vorgabe | null = null;

export function usePasswortvorgabe(): Vorgabe | null {
  const [v, setV] = useState<Vorgabe | null>(gemerkt);
  useEffect(() => {
    if (gemerkt) return;
    api.passwortregeln()
      .then((a: Vorgabe) => { gemerkt = a; setV(a); })
      .catch(() => {});
  }, []);
  return v;
}

/**
 * Was sich im Browser abhaken lässt, ohne die Sperrliste zu kennen.
 *
 * ES IST BEWUSST EINE TEILMENGE. Die Sperrliste und der Umfeldbezug stehen im
 * Server; sie hier nachzubauen hiesse, sie zweimal zu pflegen - und die
 * Abschrift wäre die veraltete. Ein Haken, der hier fehlt, kostet einen
 * Fehlversuch; eine Sperrliste, die auseinanderläuft, kostet die Regel.
 */
/**
 * Welche Regeln der Browser ÜBERHAUPT beurteilen kann.
 *
 * ORIGINALTEXT MARVIN (28.08.2026)
 *   „hier ist das passwort und die meldung vom Kamil zu 2 nicht erfüllten
 *    vorgaben. wie kann das sein?"
 *
 * SIE WAREN ERFÜLLT. Das Passwort war einwandfrei - sechzehn Zeichen, alle
 * vier Zeichenarten, nicht auf der Sperrliste, kein Bezug zu Kamils Namen
 * oder zur Firma; der Server hat es widerspruchslos angenommen. Nachgemessen
 * im Browser: bei korrekt eingetipptem Passwort blieben GENAU ZWEI Zeilen
 * ohne Haken, und es sind immer dieselben zwei - Sperrliste und Umfeld.
 *
 * DER BROWSER KANN SIE NICHT BEURTEILEN, und das ist Absicht: die Sperrliste
 * hier nachzubauen hiesse, sie zweimal zu pflegen, und die Abschrift wäre die
 * veraltete. Nur sah „kann ich nicht sagen" haargenau aus wie „noch nicht
 * erfüllt" - beides ein grauer Punkt. Wer ein gültiges Passwort eintippt und
 * zwei graue Punkte sieht, probiert weiter, und irgendwann nimmt er ein
 * schlechteres, das zufällig auch zwei graue Punkte hat.
 *
 * DREI ZUSTÄNDE BRAUCHEN DREI ZEICHEN. Diese Liste sagt, welche Zeile
 * überhaupt einen Haken bekommen KANN - unabhängig davon, was gerade im Feld
 * steht. Sie an `erfuellt() === null` festzumachen wäre falsch: `einerlei`
 * gibt bei einem zu kurzen Wert auch `null` zurück und würde beim Tippen
 * zwischen den Gruppen hin und her springen.
 *
 * `einerlei` STEHT BEWUSST NICHT DARIN, und das ist beim Bauen dieser Fassung
 * herausgekommen. Die Zeile heisst „Keine Wiederholung und keine Tastenreihe",
 * und der Browser prüfte davon nur die erste Hälfte (genug verschiedene
 * Zeichen). „xK9#mvpQrstuvwx2" hat vierzehn verschiedene Zeichen und bekam
 * einen GRÜNEN HAKEN - der Server weist es ab, denn darin steckt „rstuvwx".
 *
 * EIN FALSCHER HAKEN IST SCHLIMMER ALS EIN FEHLENDER. Beim fehlenden probiert
 * jemand weiter; beim falschen drückt er ab und bekommt eine Fehlermeldung an
 * einer Zeile, die gerade noch grün war - und ab da glaubt er der Anzeige
 * nicht mehr. Eine halbe Prüfung, die ganz aussieht, gehört nicht in den
 * Browser.
 */
const BROWSER_KANN = new Set(
  ['laenge', 'gross', 'klein', 'ziffer', 'sonder']);

function erfuellt(schluessel: string, wert: string, v: Vorgabe): boolean | null {
  if (schluessel === 'laenge') {
    return wert.length >= v.mindestlaenge && wert.length <= v.hoechstlaenge;
  }
  /* DIE VIER ZEICHENARTEN HAKEN SICH LIVE AB, und das ist bei ihnen die
     halbe Miete: „es fehlt noch ein Sonderzeichen" beim Tippen erspart den
     Versuch, der abgewiesen wird. Die Muster sind DIESELBEN wie im Server
     (`regeln/passwortregel.ts`) - Unicode-bewusst, denn „Ä" ist ein
     Grossbuchstabe und „ß" ein Kleinbuchstabe. Und das Leerzeichen zählt
     auch hier NICHT als Sonderzeichen, sonst hakt die Maske etwas ab, das
     der Server danach abweist. */
  if (schluessel === 'gross') return /\p{Lu}/u.test(wert);
  if (schluessel === 'klein') return /\p{Ll}/u.test(wert);
  if (schluessel === 'ziffer') return /\p{Nd}/u.test(wert);
  if (schluessel === 'sonder') return /[^\p{L}\p{N}\s]/u.test(wert);
  if (schluessel === 'einerlei') {
    if (wert.length < v.mindestlaenge) return null;
    return new Set(wert).size >= 5;
  }
  // „keine_vorschrift" ist keine Bedingung, sondern eine Auskunft - sie hat
  // nichts abzuhaken. Sperrliste und Umfeld weiss nur der Server.
  return null;
}

function Haken({ an }: { an: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"
         style={{ flex: 'none' }}>
      {an ? (
        <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor"
              strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <circle cx="12" cy="12" r="3.2" fill="currentColor" opacity=".35" />
      )}
    </svg>
  );
}

/**
 * Die Vorgabe, zum Hinsehen.
 *
 * `wert` ist das, was gerade im Feld steht - daran haken sich die Regeln ab,
 * die der Browser beurteilen kann. Ohne `wert` steht die Liste einfach da,
 * und das ist ebenfalls richtig: vor dem ersten Tastendruck ist noch nichts
 * erfüllt und nichts verletzt.
 */
export function Passwortregeln(
  { wert = '', verstoesse = [], schmal = false }:
  { wert?: string; verstoesse?: string[]; schmal?: boolean }
) {
  const v = usePasswortvorgabe();
  const [mehr, setMehr] = useState(false);
  if (!v) return null;

  /* ZWEI GRUPPEN, WEIL ES ZWEI SORTEN REGELN SIND. Die erste hakt sich beim
     Tippen ab, die zweite kann der Browser nicht beurteilen. Ohne die
     Trennung sehen sie gleich aus, und die zweite liest sich als „nicht
     erfüllt" - das war Kamils Meldung vom 28.08.2026. */
  const hier = v.regeln.filter((r) => BROWSER_KANN.has(r.schluessel));
  const beimAbsenden = v.regeln.filter((r) => !BROWSER_KANN.has(r.schluessel));

  const zeile = (r: Vorgabe['regeln'][number], pruefbar: boolean) => {
    const e = pruefbar ? erfuellt(r.schluessel, wert, v) : null;
    const verletzt = verstoesse.includes(r.schluessel);
    /* VIER ZUSTÄNDE: erfüllt, verletzt, offen, und „das sagt der Server".
       Ein rotes Kreuz an einer Regel, die der Browser gar nicht beurteilen
       kann, wäre eine Behauptung; ein grüner Haken daran eine falsche
       Beruhigung; und ein grauer Punkt - der alte Zustand - eine Anzeige,
       die sich nicht von „noch nicht erfüllt" unterscheidet. */
    const stand = verletzt ? 'verletzt'
      : !pruefbar ? 'serverprueft'
      : e === true ? 'erfuellt'
      : e === false && wert.length > 0 ? 'offen'
      : '';
    return (
      <li key={r.schluessel} className={stand}>
        {pruefbar
          ? <Haken an={e === true && !verletzt} />
          /* KEIN PUNKT, SONDERN EIN STRICH. Ein Punkt in derselben Spalte wie
             ein Haken liest sich als leeres Kästchen; ein Strich sagt „gilt,
             wird hier nur nicht gemessen". Bei einem echten Verstoss wird er
             rot, denn dann HAT der Server etwas gesagt. */
          : <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"
                 style={{ flex: 'none' }}>
              <path d="M6 12h12" fill="none" stroke="currentColor"
                    strokeWidth="2.6" strokeLinecap="round" />
            </svg>}
        <span>
          {r.text}
          {mehr && <span className="warum">{r.warum}</span>}
        </span>
      </li>
    );
  };

  return (
    <div className={'passwortregeln' + (schmal ? ' schmal' : '')}>
      <b className="klein">Vorgabe für Passwörter</b>
      <ul>{hier.map((r) => zeile(r, true))}</ul>
      {beimAbsenden.length > 0 && (
        <>
          {/* DER SATZ IST DIE EIGENTLICHE BEHEBUNG. Er beantwortet die Frage,
              die Kamil gestellt hat, BEVOR sie entsteht. */}
          <div className="klein leise" style={{ marginTop: 8, marginBottom: 2 }}>
            Das prüft der Server beim Absenden:
          </div>
          <ul>{beimAbsenden.map((r) => zeile(r, false))}</ul>
        </>
      )}
      <button type="button" className="alslink klein"
              onClick={() => setMehr(!mehr)}>
        {mehr ? 'weniger' : 'warum diese Regeln?'}
      </button>
      {mehr && (
        <>
          <p className="klein leise" style={{ marginTop: 6 }}>
            {v.keinRhythmus}
          </p>
          <p className="klein leise">{v.stand}</p>
        </>
      )}
    </div>
  );
}
